/**
 * La app del chofer cuenta qué le pasa (9-oct-2026).
 *
 * Lo que ocurre entre «tocó el botón» y «llegó la foto» pasa entero en el teléfono, y hasta ese día no dejaba rastro:
 * ni el teléfono, ni el navegador, ni en qué paso se cortó. Acá la app anota sus pasos (`anotar`) en el teléfono y los
 * manda juntos cuando hay señal (`enviarPendientes`), con los datos del aparato. Lo recibe `POST /chofer-app/eventos`.
 *
 * Reglas:
 *  - **Nada de acá puede romper la app ni frenar al chofer.** Todo va en try/catch y no avisa nunca.
 *  - **Se guarda sin señal y sale sola.** Los pasos esperan en el teléfono (hasta `TOPE`; con más, se van los viejos).
 *  - **No lleva datos de la operación ni la foto.** Solo el paso, el número del viaje, tiempos y tamaños.
 */
export const CLAVE_EVENTOS = "qb_chofer_eventos";
export const CLAVE_DISPOSITIVO = "qb_chofer_dispositivo";
export const CLAVE_SESION_CERRADA = "qb_sesion_cerrada";
export const TOPE = 150;
export const POR_ENVIO = 80;
export const RUTA = "/chofer-app/eventos";

// ── Lo puro ──────────────────────────────────────────────────────────────────

/** PURA. La lista después de sumar un paso, sin pasar el tope (se van los más viejos). */
export function sumar(lista, evento, tope = TOPE) {
  const base = Array.isArray(lista) ? lista : [];
  const nueva = [...base, evento];
  return nueva.length > tope ? nueva.slice(nueva.length - tope) : nueva;
}

/** PURA. Lo que queda en el teléfono después de un envío que salió bien: se quitan los `n` primeros. */
export function quitarEnviados(lista, n) {
  return Array.isArray(lista) ? lista.slice(n) : [];
}

/** PURA. `fuera` de segundos o minutos a un número entero de milisegundos acotado (un reloj que salta no ensucia). */
export function acotar(ms, maximo = 6 * 60 * 60 * 1000) {
  if (typeof ms !== "number" || !isFinite(ms) || ms < 0) return null;
  return Math.round(Math.min(ms, maximo));
}

/** PURA. Los datos del aparato a partir de lo que el navegador declara. `nav`, `pantalla` y `extra` se inyectan. */
export function armarInfo({ nav = {}, pantalla = {}, instalada = false, version = null, extra = {} } = {}) {
  const con = nav.connection || {};
  const info = {
    modelo: extra.modelo || null,
    plataforma: extra.plataforma || nav.platform || null,
    memoria_gb: typeof nav.deviceMemory === "number" ? nav.deviceMemory : null,
    modo: instalada ? "instalada" : "navegador",
    version: version || null,
    nucleos: typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null,
    pantalla: pantalla.width ? `${pantalla.width}x${pantalla.height}@${pantalla.dpr || 1}` : null,
    conexion: con.effectiveType || null,
    bajada_mbps: typeof con.downlink === "number" ? con.downlink : null,
    ahorro_datos: typeof con.saveData === "boolean" ? con.saveData : null,
    idioma: nav.language || null,
    espacio_mb: typeof extra.espacio_mb === "number" ? extra.espacio_mb : null,
    usado_mb: typeof extra.usado_mb === "number" ? extra.usado_mb : null,
  };
  Object.keys(info).forEach((k) => { if (info[k] == null) delete info[k]; });
  return info;
}

// ── Lo que toca el teléfono (nunca levanta) ──────────────────────────────────

function leerLista() {
  try {
    const crudo = localStorage.getItem(CLAVE_EVENTOS);
    const lista = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(lista) ? lista : [];
  } catch { return []; }
}
function guardarLista(lista) {
  try { localStorage.setItem(CLAVE_EVENTOS, JSON.stringify(lista)); } catch { /* cuota llena: se pierde el diagnóstico, no la foto */ }
}

/** Anota un paso. `datos` es un objeto chico de valores simples. */
export function anotar(tipo, viajeId, datos) {
  try {
    const evento = { tipo, ts: Date.now() };
    if (viajeId) evento.viaje_id = viajeId;
    if (datos && Object.keys(datos).length) evento.datos = datos;
    guardarLista(sumar(leerLista(), evento));
  } catch { /* nunca frena */ }
}

export const pendientes = () => leerLista().length;

/** El id que la app le pone a este teléfono la primera vez (no identifica a nadie: distingue un aparato de otro). */
export function idDispositivo() {
  try {
    let id = localStorage.getItem(CLAVE_DISPOSITIVO);
    if (!id || !/^[A-Za-z0-9_-]{6,40}$/.test(id)) {
      id = `d_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      localStorage.setItem(CLAVE_DISPOSITIVO, id);
    }
    return id;
  } catch { return null; }
}

let infoEnMemoria = null;
/** Los datos del aparato. Lo que tarda (modelo, espacio) se pide una vez por arranque. */
export async function infoDelTelefono() {
  if (infoEnMemoria) return infoEnMemoria;
  const extra = {};
  try {
    const uad = navigator.userAgentData;
    if (uad && uad.getHighEntropyValues) {
      const alto = await uad.getHighEntropyValues(["model", "platformVersion"]);
      if (alto.model) extra.modelo = alto.model;
      if (uad.platform) extra.plataforma = `${uad.platform} ${alto.platformVersion || ""}`.trim();
    }
  } catch { /* el navegador no lo da */ }
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const e = await navigator.storage.estimate();
      if (e.quota) extra.espacio_mb = Math.round(e.quota / 1048576);
      if (e.usage != null) extra.usado_mb = Math.round(e.usage / 1048576);
    }
  } catch { /* idem */ }
  let instalada = false;
  try {
    instalada = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  } catch { /* idem */ }
  try {
    infoEnMemoria = armarInfo({
      nav: navigator,
      pantalla: { width: window.screen.width, height: window.screen.height, dpr: Math.round((window.devicePixelRatio || 1) * 10) / 10 },
      instalada, version: process.env.REACT_APP_BUILD_INFO || "dev", extra,
    });
  } catch { infoEnMemoria = {}; }
  return infoEnMemoria;
}

let enviando = false;
/**
 * Manda lo pendiente. Devuelve cuántos pasos salieron (0 si no había, si no hay señal o si ya había un envío en
 * curso). Un servidor que todavía no conoce la ruta (404) deja los pasos donde están.
 */
export async function enviarPendientes(api) {
  if (enviando) return 0;
  const lista = leerLista();
  if (!lista.length) return 0;
  enviando = true;
  try {
    const tanda = lista.slice(0, POR_ENVIO);
    await api.post(RUTA, { dispositivo: idDispositivo(), info: await infoDelTelefono(), eventos: tanda },
      { __silent: true, timeout: 20000 });
    // Mientras salía pudieron anotarse pasos nuevos (van al final): se quitan solo los que se mandaron.
    guardarLista(quitarEnviados(leerLista(), tanda.length));
    return tanda.length;
  } catch {
    return 0;
  } finally {
    enviando = false;
  }
}

/** Deja anotado POR QUÉ se cerró una sesión de chofer (lo lee la app la próxima vez que arranca con sesión). */
export function anotarSesionCerrada(motivo) {
  try {
    const u = JSON.parse(localStorage.getItem("qb_user") || "null");
    if (!u || !["chofer_propio", "chofer_externo"].includes(u.rol)) return;
    localStorage.setItem(CLAVE_SESION_CERRADA, JSON.stringify({ motivo, ts: Date.now() }));
  } catch { /* nunca frena */ }
}

/** Al arrancar con sesión: si la anterior se cerró, lo pasa a la lista de pasos. */
export function recogerSesionCerrada() {
  try {
    const crudo = localStorage.getItem(CLAVE_SESION_CERRADA);
    if (!crudo) return;
    localStorage.removeItem(CLAVE_SESION_CERRADA);
    const { motivo, ts } = JSON.parse(crudo);
    guardarLista(sumar(leerLista(), { tipo: "sesion_cerrada", ts: ts || Date.now(), datos: { motivo: String(motivo || "") } }));
  } catch { /* nunca frena */ }
}

/** Solo para los tests. */
export function _reiniciar() { infoEnMemoria = null; enviando = false; }
